import { createPortal } from 'react-dom';
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Close } from '@sbt-web/ui';
import styles from './index.module.scss';

interface TextualReviewModalProps {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
}

interface SentinelProps {
  className: string;
  tabIndex?: number;
}

const Sentinel = forwardRef<HTMLDivElement, SentinelProps>(
  ({ tabIndex = 0, className }, ref) => (
    <div className={className} tabIndex={tabIndex} ref={ref}></div>
  )
);

Sentinel.displayName = 'Sentinel';

export const TextualReviewModal = ({
  children,
  open,
  onClose,
}: TextualReviewModalProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sentinelTopRef = useRef<HTMLDivElement>(null);
  const sentinelBottomRef = useRef<HTMLDivElement>(null);
  const handlerElementRef = useRef<HTMLElement | null>(null);
  const firstUpdateRef = useRef<boolean>(true);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  //this ref is useful to fire onClose callback only when the modal is closed
  const closingRef = useRef<boolean>(false);

  const handleClose = useCallback(() => {
    const { current } = wrapperRef;

    if (current) {
      closingRef.current = true;
      current.classList.add(styles.is_hidden);
    }
  }, []);

  const handleAnimationEnd = () => {
    //if modal is closed, onClose callback is fired
    if (closingRef.current) {
      closingRef.current = false;
      const { current } = handlerElementRef;
      if (current) {
        current.focus();
      }

      setIsMounted(false);
      onClose();
    }
  };

  useEffect(() => {
    if (firstUpdateRef.current) {
      firstUpdateRef.current = false;
    }
  }, []);

  useEffect(() => {
    //focus DOM element that opening the modal
    if (open) {
      handlerElementRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    }

    // remove display none css prop, opening the panel, otherwise the css animation does not works
    if (open && !firstUpdateRef.current) {
      const { current } = wrapperRef;

      if (current) {
        current.style.display = '';
      }

      const onKeyDownCallback = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          handleClose();
        }

        // accessibility trick to keep the focus on modal elements
        if (
          (event.shiftKey &&
            event.key === 'Tab' &&
            document.activeElement === sentinelTopRef.current) ||
          (!event.shiftKey &&
            event.key === 'Tab' &&
            document.activeElement === sentinelBottomRef.current)
        ) {
          event.preventDefault();
        }
      };

      window.addEventListener('keydown', onKeyDownCallback);

      return () => {
        window.removeEventListener('keydown', onKeyDownCallback);
      };
    }
  }, [open, handleClose]);

  //this must stay below keydown listener useEffect
  useEffect(() => {
    const { current } = sentinelTopRef;

    if (open) {
      current?.focus();
    }
  }, [open]);

  useEffect(() => {
    const shouldSetIsMounted = open && !isMounted;

    if (shouldSetIsMounted) {
      setIsMounted(true);
    } else if (!open && isMounted) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, handleClose]);

  if (isMounted) {
    return createPortal(
      <div className={styles.wrapper} ref={wrapperRef} role="presentation">
        <div
          aria-hidden="true"
          className={styles.backdrop}
          onClick={handleClose}
        ></div>
        <Sentinel className={styles.sentinel_top} ref={sentinelTopRef} />
        <div
          className={styles.container}
          onAnimationEnd={handleAnimationEnd}
          role="dialog"
          aria-label="Recensioni dell'utente"
          aria-modal="true"
        >
          <header className={styles.header}>
            <button
              onClick={handleClose}
              className={styles.close_button}
              aria-label="Chiudi"
              type="button"
              tabIndex={0}
            >
              <Close width={24} height={24} />
            </button>
            <p className="body-text semibold">Recensioni</p>
          </header>
          <div className={styles.body}>{children}</div>
        </div>
        <Sentinel className={styles.sentinel_bottom} ref={sentinelBottomRef} />
      </div>,
      document.body
    );
  }

  return null;
};
